// src/firebase/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from './firebase';
import { trackLogin, trackSignup, trackLogout } from '../utils/analytics';
import { setUserContext } from '../utils/errorHandling';

// Create the authentication context
const AuthContext = createContext();

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component that wraps your app and makes auth available
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutCallbacks, setLogoutCallbacks] = useState([]);

  // Register a callback to be called on logout
  const registerLogoutCallback = useCallback((callback) => {
    console.log('AuthContext - Registering logout callback');
    setLogoutCallbacks(prev => [...prev, callback]);
    return () => {
      console.log('AuthContext - Unregistering logout callback');
      setLogoutCallbacks(prev => prev.filter(cb => cb !== callback));
    };
  }, []);

  // Sign in function
  const login = useCallback((email, password) => {
    console.log('AuthContext - Login attempt');
    return signInWithEmailAndPassword(auth, email, password)
      .then((result) => {
        trackLogin('email'); // 📊 TRACK LOGIN
        return result;
      });
  }, []);

  // Sign up function
  const signup = useCallback(async (email, password) => {
    console.log('AuthContext - Signup attempt');
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Send email verification
    if (userCredential.user) {
      console.log('AuthContext - Sending email verification');
      await sendEmailVerification(userCredential.user);
      trackSignup('email');
    }

    return userCredential;
  }, []);

  // Google sign in function
  const signInWithGoogle = useCallback(async () => {
    console.log('AuthContext - Google sign in attempt');
    const provider = new GoogleAuthProvider();

    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      const result = await signInWithPopup(auth, provider);
      console.log('AuthContext - Google sign in successful');
      trackLogin('google');
      return result;
    } catch (error) {
      console.error('AuthContext - Google sign in error:', error);

      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Pop-up was blocked by the browser. Please allow pop-ups and try again.');
      } else {
        throw error;
      }
    }
  }, []);

  // Microsoft sign in function
  const signInWithMicrosoft = useCallback(async () => {
    console.log('AuthContext - Microsoft sign in attempt');
    const provider = new OAuthProvider('microsoft.com');

    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      const result = await signInWithPopup(auth, provider);
      console.log('AuthContext - Microsoft sign in successful');
      trackLogin('microsoft');
      return result;
    } catch (error) {
      console.error('AuthContext - Microsoft sign in error:', error);

      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Pop-up was blocked by the browser. Please allow pop-ups and try again.');
      } else {
        throw error;
      }
    }
  }, []);

  // Password reset function
  const resetPassword = useCallback((email) => {
    console.log('AuthContext - Password reset attempt for:', email);
    return sendPasswordResetEmail(auth, email);
  }, []);

  // Resend verification email function
  const resendVerificationEmail = useCallback(async () => {
    console.log('AuthContext - Resending verification email');
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }
    
    if (auth.currentUser.emailVerified) {
      throw new Error('Email is already verified');
    }
    
    return sendEmailVerification(auth.currentUser);
  }, []);

  // Refresh user to check email verification status
  const refreshUser = useCallback(async () => {
    console.log('AuthContext - Refreshing user data');
    if (!auth.currentUser) {
      throw new Error('No user is currently signed in');
    }
    
    await auth.currentUser.reload();
    // Force state update with new object reference so React knows to re-render
    setCurrentUser({ ...auth.currentUser });
    console.log('AuthContext - User data refreshed, emailVerified:', auth.currentUser.emailVerified);
  }, []);

  // Sign out function
  const logout = useCallback(async () => {
    console.log('AuthContext - Logout initiated with', logoutCallbacks.length, 'callbacks');

    // Execute all registered logout callbacks
    for (const callback of logoutCallbacks) {
      try {
        console.log('AuthContext - Executing logout callback');
        await callback();
      } catch (error) {
        console.error("Error in logout callback:", error);
      }
    }

    // Sign out from Firebase
    console.log('AuthContext - Signing out from Firebase');
    trackLogout();
    return signOut(auth);
  }, [logoutCallbacks]);

  // Set up an observer for changes to the user's sign in state
  useEffect(() => {
    console.log('AuthContext - Setting up auth state observer');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('AuthContext - Auth state changed:', user ? `User ${user.uid} logged in` : 'No user');
      if (user) {
        console.log('AuthContext - Email verified:', user.emailVerified);
      }
      setCurrentUser(user);
      setLoading(false);
      // Set user context in Sentry when auth state changes
      setUserContext(user);
    });
    
    return unsubscribe;
  }, []);

  // The value passed to the context
  const contextValue = React.useMemo(() => ({
    currentUser,
    login,
    signup,
    signInWithGoogle,
    signInWithMicrosoft,
    resetPassword,
    resendVerificationEmail,
    refreshUser,
    logout,
    registerLogoutCallback,
  }), [currentUser, login, signup, signInWithGoogle, signInWithMicrosoft, resetPassword, resendVerificationEmail, refreshUser, logout, registerLogoutCallback]);

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};